import axios from 'axios';
import { handleUnauthorizedSession } from '../utils/authSession';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const API = `${API_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add authorization token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log('Token attached to request:', config.url);
    } else {
      console.warn('No token found for request:', config.url);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle 401 errors (unauthorized)
api.interceptors.response.use(
  (response) => {
    console.log('API Response:', response.status, response.config.url);
    return response;
  },
  (error) => {
    console.error('API Error:', error.response?.status, error.config?.url);
    if (error.response?.status === 401) {
      handleUnauthorizedSession({ requestUrl: error.config?.url });
    }
    return Promise.reject(error);
  }
);

// ==================== DRIVERS ====================

export const getDrivers = async (status = null) => {
  const params = status ? { status } : {};
  const response = await api.get('/drivers', { params });
  return response.data;
};

export const getDriver = async (driverId) => {
  const response = await api.get(`/drivers/${driverId}`);
  return response.data;
};

export const createDriver = async (driverData) => {
  const response = await api.post('/drivers', driverData);
  return response.data;
};

export const updateDriver = async (driverId, updateData) => {
  const response = await api.put(`/drivers/${driverId}`, updateData);
  return response.data;
};

export const updateDriverStatus = async (driverId, status) => {
  const response = await api.put(`/drivers/${driverId}/status?status=${status}`);
  return response.data;
};

export const updateDriverLocation = async (driverId, position) => {
  const response = await api.put(`/drivers/${driverId}/location`, position);
  return response.data;
};

export const deleteDriver = async (driverId) => {
  const response = await api.delete(`/drivers/${driverId}`);
  return response.data;
};

// ==================== DRIVER CREDENTIALS & MANAGEMENT ====================

export const getDriverCredentials = async (driverId) => {
  const response = await api.get(`/drivers/${driverId}/credentials`);
  return response.data;
};

export const resetDriverPassword = async (driverId) => {
  const response = await api.post(`/drivers/${driverId}/reset-password`);
  return response.data;
};

export const suspendDriver = async (driverId, reason = null) => {
  const response = await api.post(`/drivers/${driverId}/suspend`, null, { params: { reason } });
  return response.data;
};

export const activateDriver = async (driverId) => {
  const response = await api.post(`/drivers/${driverId}/activate`);
  return response.data;
};

export const updateDriverPermit = async (driverId, permitStatus) => {
  const response = await api.patch(`/drivers/${driverId}/permit`, {
    permit_status: permitStatus,
  });
  return response.data;
};

// ==================== DRIVER WALLET & CASH HANDOVER ====================

export const getDriverWallet = async (driverId) => {
  const response = await api.get(`/drivers/${driverId}/wallet`);
  return response.data;
};

export const addToDriverWallet = async (driverId, amount, rideId = null, notes = null) => {
  const response = await api.post(`/drivers/${driverId}/wallet/add`, {
    amount,
    ride_id: rideId,
    notes
  });
  return response.data;
};

export const createCashHandover = async (handoverData) => {
  const response = await api.post('/cash-handover', handoverData);
  return response.data;
};

export const getCashHandovers = async (filters = {}) => {
  const response = await api.get('/cash-handover', { params: filters });
  return response.data;
};

export const getCashHandoverStats = async () => {
  const response = await api.get('/cash-handover/stats');
  return response.data;
};

// ==================== PASSENGERS ====================

export const getPassengers = async () => {
  const response = await api.get('/passengers');
  return response.data;
};

export const getPassenger = async (passengerId) => {
  const response = await api.get(`/passengers/${passengerId}`);
  return response.data;
};

export const createPassenger = async (passengerData) => {
  const response = await api.post('/passengers', passengerData);
  return response.data;
};

export const updatePassenger = async (passengerId, updateData) => {
  const response = await api.put(`/passengers/${passengerId}`, updateData);
  return response.data;
};

export const deletePassenger = async (passengerId) => {
  const response = await api.delete(`/passengers/${passengerId}`);
  return response.data;
};

// ==================== RIDES ====================

export const getRides = async (filters = {}) => {
  const response = await api.get('/rides', { params: filters });
  return response.data;
};

export const getPendingRides = async () => {
  const response = await api.get('/rides/pending');
  return response.data;
};

export const getRide = async (rideId) => {
  const response = await api.get(`/rides/${rideId}`);
  return response.data;
};

export const createRide = async (rideData) => {
  const response = await api.post('/rides', rideData);
  return response.data;
};

export const assignDriver = async (rideId, driverId) => {
  const response = await api.put(`/rides/${rideId}/assign?driver_id=${driverId}`);
  return response.data;
};

export const updateRideStatus = async (rideId, status) => {
  const response = await api.put(`/rides/${rideId}/status?status=${status}`);
  return response.data;
};

export const getRideOperations = async ({ tab = 'active', hours = 2 } = {}) => {
  const response = await api.get('/admin/rides/operations', { params: { tab, hours } });
  return response.data;
};

export const getRideStats = async () => {
  const response = await api.get('/admin/rides/stats');
  return response.data;
};

export const deleteRide = async (rideId) => {
  const response = await api.delete(`/admin/rides/${rideId}`);
  return response.data;
};

export const resolveRide = async (rideId, { action, reason, paymentMethod = 'cash', amountReceived } = {}) => {
  const response = await api.post(`/admin/rides/${rideId}/resolve`, {
    action,
    reason,
    payment_method: paymentMethod,
    amount_received: amountReceived,
  });
  return response.data;
};

// ==================== FINANCE ====================

export const syncInvoices = async () => {
  const response = await api.post('/finance/sync-invoices');
  return response.data;
};

export const downloadInvoicePdf = async (invoiceId, invoiceNumber = 'fature') => {
  const response = await api.get(`/invoices/${invoiceId}/pdf`, { responseType: 'blob' });
  const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `${invoiceNumber}.pdf`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const getFinanceSummary = async (params = {}) => {
  const response = await api.get('/finance/summary', { params });
  return response.data;
};

export const getCashUnsettled = async () => {
  const response = await api.get('/cash/unsettled-totals');
  return response.data;
};

export const getDailyClosingRevenue = async (params = {}) => {
  const response = await api.get('/daily-closing/revenue', { params });
  return response.data;
};

export const dispatcherCancelRide = async (rideId, reason) => {
  const response = await api.post('/dispatcher/cancel', {
    ride_id: rideId,
    reason,
  });
  return response.data;
};

export const rateRide = async (rideId, rating) => {
  const response = await api.put(`/rides/${rideId}/rate?rating=${rating}`);
  return response.data;
};

// ==================== STATISTICS ====================

export const getDailyStats = async () => {
  const response = await api.get('/stats/daily');
  return response.data;
};

export const getWeeklyStats = async () => {
  const response = await api.get('/stats/weekly');
  return response.data;
};

export const getOverviewStats = async () => {
  const response = await api.get('/stats/overview');
  return response.data;
};

export const getStats = async () => {
  const response = await api.get('/stats/overview');
  return response.data;
};

export const getLiveDashboardStats = async () => {
  const response = await api.get('/stats/live-dashboard');
  return response.data;
};

export const getHourlyStats = async (hours = 24) => {
  const response = await api.get('/stats/hourly', { params: { hours } });
  return response.data;
};

// ==================== DRIVER SHIFTS ====================

export const getDriverShifts = async (driverId = null) => {
  const params = { limit: 200 };
  if (driverId) params.driver_id = driverId;
  const response = await api.get('/drivers/shifts', { params });
  return response.data;
};

// ==================== AUTH ====================

export const requestOTP = async (phone, userType) => {
  const response = await api.post('/auth/login', { phone, user_type: userType });
  return response.data;
};

export const verifyOTP = async (phone, otp, userType) => {
  const response = await api.post('/auth/verify', { phone, otp, user_type: userType });
  return response.data;
};

// ==================== SEED ====================

export const seedDatabase = async () => {
  const response = await api.post('/seed');
  return response.data;
};

export default api;
